import React, { useEffect, useRef, useState } from 'react';
import { Modal, Button } from 'antd';
import { BellOutlined, CloseOutlined, EyeOutlined, FileOutlined } from '@ant-design/icons';
import axios from 'axios';
import { useTheme } from '@/context/theme';

const GlobalReminderModal = () => {
    const { isDarkMode } = useTheme();
    const [isReminderPopupVisible, setIsReminderPopupVisible] = useState(false);
    const [currentReminder, setCurrentReminder] = useState(null);
    const [isPlaying, setIsPlaying] = useState(false);
    const audioRef = useRef(null);

    useEffect(() => {
        // Shared audio instance
        audioRef.current = new Audio('/mixkit-happy-bells-notification-937.wav');
        audioRef.current.loop = true;

        const handleReminderEvent = async (event) => {
            const data = event.detail;
            console.log("[GlobalReminderModal] Reminder Received:", data);

            // Allow both 'reminder' and 'new_notification' (if type is reminder)
            const isReminderType = data.type === 'reminder' || data.notificationType === 'reminder';

            if (isReminderType && data.relatedData && data.relatedData.reminderId) {
                try {
                    const res = await axios.get(`/calendarreminder/${data.relatedData.reminderId}`);
                    if (res.data && res.data.reminder) {
                        setCurrentReminder(res.data.reminder);
                        setIsReminderPopupVisible(true);
                        playSound();
                    }
                } catch (err) {
                    console.error("[GlobalReminderModal] Failed to fetch reminder details:", err);
                }
            }
        };

        window.addEventListener('reminder_received', handleReminderEvent);
        // Also listen to the standard notification event if your app dispatches it to window
        // Note: socketService emits via socket, but does your app dispatch it to window?
        // Assuming your main App.jsx or socket listener dispatches 'new_notification' to window.
        // If not, we might need to hook into socket here directly or ensure App.jsx dispatches it.
        window.addEventListener('new_notification', handleReminderEvent);

        return () => {
            window.removeEventListener('reminder_received', handleReminderEvent);
            window.removeEventListener('new_notification', handleReminderEvent);
            stopSound();
        };
    }, []);

    const playSound = () => {
        if (audioRef.current) {
            audioRef.current.play().catch(e => console.error('[GlobalReminderModal] Error playing sound:', e));
            setIsPlaying(true);
        }
    };

    const stopSound = () => {
        if (audioRef.current) {
            audioRef.current.pause();
            audioRef.current.currentTime = 0;
            setIsPlaying(false);
        }
    };

    const handleCloseReminderPopup = () => {
        stopSound();
        setIsReminderPopupVisible(false);
        setCurrentReminder(null);
    };

    const getFilePreview = (attachment) => {
        if (!attachment) return null;

        const fileUrl = `${process.env.REACT_APP_BASE_URL}uploads/${attachment}`;
        const isImage = /\.(jpg|jpeg|png|gif|bmp|webp)$/i.test(attachment);

        return (
            <div style={{ marginTop: '10px' }}>
                {isImage ? (
                    <div style={{ display: 'flex', alignItems: 'center', gap: '10px', justifyContent: 'center' }}>
                        <img
                            src={fileUrl}
                            alt="Attachment"
                            style={{
                                maxWidth: '100px',
                                maxHeight: '100px',
                                objectFit: 'cover',
                                borderRadius: '4px'
                            }}
                        />
                        <Button
                            icon={<EyeOutlined />}
                            size="small"
                            onClick={() => window.open(fileUrl, '_blank')}
                        >
                            View Full Size
                        </Button>
                    </div>
                ) : (
                    <div style={{ display: 'flex', alignItems: 'center', gap: '10px', justifyContent: 'center' }}>
                        <FileOutlined style={{ fontSize: '24px', color: '#1890ff' }} />
                        <span>{attachment}</span>
                        <Button
                            icon={<EyeOutlined />}
                            size="small"
                            onClick={() => window.open(fileUrl, '_blank')}
                        >
                            Open File
                        </Button>
                    </div>
                )}
            </div>
        );
    };

    return (
        <Modal
            title={
                <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                    <BellOutlined style={{ color: '#ff4d4f', fontSize: '20px' }} />
                    <span style={{ color: '#ff4d4f', fontWeight: 'bold' }}>Global Reminder Alert!</span>
                    {isPlaying && (
                        <div style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: '5px',
                            backgroundColor: '#fff2f0',
                            padding: '2px 8px',
                            borderRadius: '12px',
                            border: '1px solid #ffccc7'
                        }}>
                            <div style={{
                                width: '8px',
                                height: '8px',
                                backgroundColor: '#ff4d4f',
                                borderRadius: '50%',
                                animation: 'pulse 1.5s infinite'
                            }}></div>
                            <span style={{ fontSize: '12px', color: '#ff4d4f' }}>Playing</span>
                        </div>
                    )}
                </div>
            }
            visible={isReminderPopupVisible}
            onCancel={handleCloseReminderPopup}
            footer={[
                <Button
                    key="close"
                    type="primary"
                    danger
                    icon={<CloseOutlined />}
                    onClick={handleCloseReminderPopup}
                    size="large"
                    style={{ width: '100%', height: '50px', borderRadius: '8px' }}
                >
                    Stop Sound & Close
                </Button>
            ]}
            width={500}
            centered
            closable={false}
            maskClosable={false}
            bodyStyle={{
                padding: '24px',
                background: isDarkMode
                    ? 'linear-gradient(135deg, #3d1a17 0%, #2d2d2d 100%)'
                    : 'linear-gradient( 135deg, #fff2f0 0%, #ffffff 100%)',
            }}
        >
            {currentReminder && (
                <div style={{ textAlign: 'center' }}>
                    <div style={{
                        fontSize: '24px',
                        fontWeight: 'bold',
                        color: '#1890ff',
                        marginBottom: '16px'
                    }}>
                        {currentReminder.title}
                    </div>

                    <div style={{
                        fontSize: '16px',
                        color: '#666',
                        marginBottom: '12px'
                    }}>
                        <strong>Time:</strong> {new Date(currentReminder.dateTime).toLocaleString()}
                    </div>

                    {currentReminder.user?.name && (
                        <div style={{
                            fontSize: '15px',
                            color: '#444',
                            marginBottom: '8px'
                        }}>
                            <strong>Organizer:</strong> {currentReminder.user.name}
                        </div>
                    )}

                    {currentReminder.selectedUsers && currentReminder.selectedUsers.length > 0 && (
                        <div style={{
                            fontSize: '15px',
                            color: '#444',
                            marginBottom: '12px'
                        }}>
                            <strong>Participants:</strong> {currentReminder.selectedUsers.map(u => u.name).join(', ')}
                        </div>
                    )}

                    {currentReminder.notes && (
                        <div style={{
                            fontSize: '14px',
                            color: '#666',
                            marginBottom: '12px',
                            padding: '12px',
                            backgroundColor: '#f9f9f9',
                            borderRadius: '6px',
                            border: '1px solid #e8e8e8',
                            textAlign: 'left'
                        }}>
                            <strong>Agenda:</strong> {currentReminder.notes}
                        </div>
                    )}

                    {currentReminder.meetLink && (
                        <div style={{ marginBottom: '12px' }}>
                            <Button
                                type="link"
                                href={currentReminder.meetLink}
                                target="_blank"
                                style={{ fontSize: '16px' }}
                            >
                                🔗 Join Meeting
                            </Button>
                        </div>
                    )}

                    {currentReminder.attachment && getFilePreview(currentReminder.attachment)}
                </div>
            )}

            <style dangerouslySetInnerHTML={{
                __html: `
        @keyframes pulse {
          0% { opacity: 1; transform: scale(1); }
          50% { opacity: 0.5; transform: scale(1.2); }
          100% { opacity: 1; transform: scale(1); }
        }
      `}} />
        </Modal>
    );
};

export default GlobalReminderModal;
